import { useEffect, useState, useCallback } from "react";
import { CiSearch } from "react-icons/ci";
import { CiCirclePlus } from "react-icons/ci";
import toast from "react-hot-toast";
import "../../styles/adminUsers.css";
import Loader from "../../components/Loader";
import NoData from "../../components/NoData";
import AddAdminUserModal from "./modals/AddAdminUserModal";
import AdminUsersServices from "../../services/AdminUsersServices";
import { getAvatarColor, getInitials } from "../../utils/utils";
import { MdEdit, MdDelete, MdLockReset, MdImage } from "react-icons/md";
import { LiaWalletSolid } from "react-icons/lia";
import EditAdminModal from "./modals/EditAdminModal";
import ConfirmDeleteModal from "../../components/ConfirmDeleteModal";
import AddWalletAmountModal from "./modals/AddWalletAmountModal";
import ResetPasswordModal from "../../components/ResetPasswordModal";
import PasswordServices from "../../services/PasswordServices";
import AssignTemplateModal from "../../components/AssignTemplateModal";

const AdminManage = () => {
  const adminSerApi = new AdminUsersServices();
  const passwordApi = new PasswordServices();

  const [modalIsOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [adminUserList, setAdminUserList] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchFilter, setSearchFilter] = useState("");
  const [pagination, setPagination] = useState(null);
  const [editAdminModal, setEditAdminModal] = useState(false);
  const [editAdminData, setEditAdminData] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [walletModal, setWalletModal] = useState(false);
  const [walletUser, setWalletUser] = useState(null);
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [resetLoading, setResetLoading] = useState(false);

  const getAllAdminUserData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await adminSerApi.getAllAdminuserData(
        page,
        limit,
        searchFilter,
      );

      if (response.isSuccess) {
        setAdminUserList(response.data || []);
        setPagination(response.pagination);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error("Failed to load admin users");
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchFilter]);

  const toggleUserStatus = async (userData) => {
    const newStatus = userData.is_active === 1 ? 0 : 1;

    try {
      const response = await adminSerApi.toggleAdminStatus({
        id: userData.id,
        is_active: newStatus,
      });

      if (response.isSuccess) {
        toast.success(response.message);
        setAdminUserList((prev) =>
          prev.map((u) =>
            u.id === userData.id ? { ...u, is_active: newStatus } : u,
          ),
        );
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      const msg =
        error?.response?.data?.message || "Failed to update user status";
      toast.error(msg);
    }
  };

  const openEditModal = async (userData) => {
    setEditAdminModal(true);
    setEditAdminData(userData);
  };

  const confirmDelete = async (userData) => {
    setSelectedUser(userData);
    setDeleteModalOpen(true);
  };

  // Image template on an admin: inherited by every outlet and player under
  // them that has none of their own.
  const [assignTplModal, setAssignTplModal] = useState(false);
  const [assignTplUser, setAssignTplUser] = useState(null);
  const openAssignTemplate = (userData) => {
    setAssignTplUser(userData);
    setAssignTplModal(true);
  };
  const closeAssignTemplate = () => {
    setAssignTplModal(false);
    setAssignTplUser(null);
  };
  const handleAssignTemplate = (payload) => adminSerApi.assignTemplate(payload);

  const openResetPasswordModal = (userData) => {
    setResetUser(userData);
    setResetModalOpen(true);
  };

  const openWalletModal = (userData) => {
    setWalletUser(userData);
    setWalletModal(true);
  };
  const handleSearch = (e) => {
    setPage(1); // reset page
    setSearchFilter(e.target.value);
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    adminSerApi
      .deleteAdminUser(selectedUser.id)
      .then((response) => {
        if (response.isSuccess) {
          toast.success(response.message);
          getAllAdminUserData();
        } else {
          toast.error(response.message);
        }
      })
      .catch((error) => {
        console.error("Error deleteAdminUser:", error);
        const backendMessage =
          error?.response?.data?.message || "Failed to delete admin user";

        toast.dismiss();
        toast.error(backendMessage);
      })
      .finally(() => {
        setDeleteLoading(false);
        setDeleteModalOpen(false);
        setSelectedUser(null);
      });
  };

  const handleResetPassword = async (newPassword) => {
    if (!resetUser) return;
    setResetLoading(true);
    try {
      const response = await passwordApi.resetUserPassword({
        userId: resetUser.id,
        newPassword,
        description: `Admin password reset for user ${resetUser.username}`,
      });
      if (response.isSuccess) {
        toast.success(response.message || "Password reset successfully");
        setResetModalOpen(false);
        setResetUser(null);
      } else {
        toast.error(response.message || "Failed to reset password");
      }
    } catch (error) {
      const backendMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Failed to reset password";
      toast.error(backendMessage);
    } finally {
      setResetLoading(false);
    }
  };

  useEffect(() => {
    getAllAdminUserData();
  }, [getAllAdminUserData]);

  const Avatar = ({ name, image }) => {
    if (image) {
      return <img src={image} alt={name} className="admin-avatar" />;
    }

    const initials = getInitials(name);
    const bgColor = getAvatarColor(name);

    return (
      <div
        className="admin-avatar fallback-avatar"
        style={{ backgroundColor: bgColor }}
      >
        {initials}
      </div>
    );
  };

  return (
    <div className="admin-manage-view">
      <div className="admin_user-header">
        <div className="admin-header-row">
          {/* SEARCH */}
          <div className="admin-search">
            <div className="search-box">
              <CiSearch size={18} className="search-icon" />
              <input
                type="text"
                className="form-control search-input"
                placeholder="Search admin"
                value={searchFilter}
                onChange={handleSearch}
              />
            </div>
          </div>

          {/* ACTION */}
          <button
            className="add_admin-btn"
            onClick={() => {
              setIsOpen(true);
            }}
          >
            <CiCirclePlus size={18} />
            Add Admin
          </button>
        </div>
      </div>

      <div>
        {loading ? (
          <Loader message="Loading Admin Users..." />
        ) : adminUserList.length === 0 ? (
          <NoData
            message="No Admin Users Found"
            subText="You haven't added any admin users yet. Click on Add Admin to get started."
          />
        ) : (
          <>
            <div className="admin-table-header">
              <div className="col-user">User</div>
              <div className="col-mobile">Mobile</div>
              <div className="col-role">Role</div>
              <div className="col-status">Status</div>
              <div className="col-action">Action</div>
            </div>

            <div className="admin-table">
              {adminUserList.map((user) => (
                <div className="admin-row" key={user.id}>
                  {/* USER */}
                  <div className="admin-user">
                    <Avatar name={user.name} image={user.profile_image} />
                    <div>
                      <div className="admin-name">{user.name}</div>
                      <div className="admin-username">({user.username})</div>
                      <div className="admin-username">
                        Wallet balance: <span className="wallet"> {user?.wallet_balance ?? 0}</span>
                      </div>
                      {user?.template_id && user?.template_name && (
                        <div
                          className="admin-template-badge"
                          title="Outlets and players under this admin without their own template use this one"
                        >
                          <MdImage size={14} />
                          <span>{user.template_name}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="admin-row-meta">
                    {/* NICK NAME */}
                    <div className="admin-col">
                      <div className="admin-username">{user.mobile}</div>
                    </div>

                    {/* ACCOUNT */}
                    <div className="admin-col">
                      <span className="badge badge-basic">Admin</span>
                    </div>

                    {/* ACTIVE SWITCH */}
                    <div className="admin-col">
                      <label className="switch">
                        <input
                          type="checkbox"
                          checked={user.is_active === 1}
                          onChange={() => toggleUserStatus(user)}
                        />
                        <span className="slider round"></span>
                      </label>
                    </div>
                  </div>

                  {/* ACTIONS */}
                  <div className="admin-actions">
                    <button
                      className="icon-btn edit"
                      title="Edit User"
                      onClick={() => openEditModal(user)}
                    >
                      <MdEdit size={18} />
                    </button>

                    <button
                      className="icon-btn delete"
                      title="Delete User"
                      onClick={() => confirmDelete(user)}
                    >
                      <MdDelete size={18} />
                    </button>

                    <button
                      className="icon-btn edit"
                      title="Reset Password"
                      onClick={() => openResetPasswordModal(user)}
                    >
                      <MdLockReset size={18} />
                    </button>

                    <button
                      className="icon-btn edit"
                      title="Add wallet balance"
                      onClick={() => openWalletModal(user)}
                    >
                      <LiaWalletSolid size={18} />
                    </button>

                    <button
                      className="icon-btn edit"
                      title={user.template_id ? "Change template" : "Assign template"}
                      onClick={() => openAssignTemplate(user)}
                    >
                      <MdImage size={18} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {pagination && pagination.totalPages > 1 && (
              <div className="modern-pagination">
                {/* Limit Selector */}
                <div className="mp-limit">
                  <span>Show</span>
                  <select
                    value={limit}
                    onChange={(e) => setLimit(e.target.value)}
                  >
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                </div>

                {/* Navigation */}
                <div className="mp-controls">
                  <button
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                  >
                    Prev
                  </button>

                  <div className="mp-info">
                    Page <strong>{pagination.page}</strong> of{" "}
                    <strong>{pagination.totalPages}</strong>
                  </div>

                  <button
                    disabled={page === pagination.totalPages}
                    onClick={() => setPage(page + 1)}
                  >
                    Next
                  </button>
                </div>

                {/* Jump to Page */}
                <div className="mp-jump">
                  <select
                    value={page}
                    onChange={(e) => setPage(e.target.value)}
                  >
                    {Array.from({ length: pagination.totalPages }, (_, i) => (
                      <option key={i + 1} value={i + 1}>
                        Page {i + 1}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <AddAdminUserModal
        modalIsOpen={modalIsOpen}
        setIsOpen={setIsOpen}
        getAllAdminUserData={getAllAdminUserData}
      />

      <EditAdminModal
        editAdminModal={editAdminModal}
        setEditAdminModal={setEditAdminModal}
        editAdminData={editAdminData}
        setEditAdminData={setEditAdminData}
        getAllAdminUserData={getAllAdminUserData}
      />

      <ConfirmDeleteModal
        show={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDelete}
        title="Delete Admin"
        message={`Are you sure you want to delete "${selectedUser?.name}"?`}
        loading={deleteLoading}
      />

      <AddWalletAmountModal
        walletModal={walletModal}
        setWalletModal={setWalletModal}
        walletUser={walletUser}
        setWalletUser={setWalletUser}
        setAdminUserList={setAdminUserList}
      />

      <AssignTemplateModal
        show={assignTplModal}
        onClose={closeAssignTemplate}
        targetUser={assignTplUser}
        onAssign={handleAssignTemplate}
        onSaved={getAllAdminUserData}
      />

      <ResetPasswordModal
        show={resetModalOpen}
        onClose={() => {
          if (resetLoading) return;
          setResetModalOpen(false);
          setResetUser(null);
        }}
        onConfirm={handleResetPassword}
        userName={resetUser?.name}
        loading={resetLoading}
        title="Reset Admin Password"
      />
    </div>
  );
};

export default AdminManage;
